import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import toast from 'react-hot-toast';
import { authApi } from '@/services/api';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Mail, ArrowLeft, Lock, KeyRound } from 'lucide-react';

const emailSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
});

const passwordSchema = z.object({
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  const emailForm = useForm({ resolver: zodResolver(emailSchema) });
  const passwordForm = useForm({ resolver: zodResolver(passwordSchema) });

  const onEmailSubmit = async (data: any) => {
    try {
      setIsLoading(true);
      await authApi.forgotPassword(data.email);
      setEmail(data.email);
      setStep(2);
      toast.success('If this email exists, an OTP has been sent.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to request reset');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const onOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      return toast.error('Please enter the full 6-digit OTP');
    }
    setStep(3);
  };

  const onPasswordSubmit = async (data: any) => {
    try {
      setIsLoading(true);
      await authApi.resetPassword(email, otp.join(''), data.password);
      toast.success('Password reset successfully! Please log in.');
      navigate('/login');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <img src="/logos/Neuralnet_logo.png" alt="NeuralNet Logo" className="h-10 mx-auto" />
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 relative overflow-hidden">
          {/* Progress bar */}
          <div className="absolute top-0 left-0 w-full h-1 bg-slate-100">
            <div 
              className="h-full bg-blue-600 transition-all duration-500 ease-out"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>

          <Link to="/login" className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-slate-800 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to login
          </Link>

          {step === 1 && (
            <div className="animate-slide-in-right">
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                <KeyRound className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Forgot password?</h2>
              <p className="text-slate-500 mb-6">No worries, we'll send you reset instructions.</p>

              <form onSubmit={emailForm.handleSubmit(onEmailSubmit)} className="space-y-4">
                <Input
                  label="Email Address"
                  placeholder="you@neuralnet.in"
                  {...emailForm.register('email')}
                  error={emailForm.formState.errors.email?.message?.toString()}
                  leftIcon={<Mail className="w-5 h-5" />}
                />
                <Button type="submit" fullWidth size="lg" isLoading={isLoading}>
                  Reset Password
                </Button>
              </form>
            </div>
          )}

          {step === 2 && (
            <div className="animate-slide-in-right">
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Check your email</h2>
              <p className="text-slate-500 mb-6">We sent a 6-digit code to <span className="font-medium text-slate-700">{email}</span></p>

              <form onSubmit={onOtpSubmit} className="space-y-6">
                <div className="flex justify-between gap-2">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => { otpRefs.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className="w-12 h-14 text-center text-xl font-bold rounded-lg border-slate-300 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
                    />
                  ))}
                </div>
                <Button type="submit" fullWidth size="lg" disabled={otp.join('').length !== 6}>
                  Verify Code
                </Button>
              </form>
            </div>
          )}

          {step === 3 && (
            <div className="animate-slide-in-right">
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Set new password</h2>
              <p className="text-slate-500 mb-6">Your new password must be different from previous used passwords.</p>

              <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
                <Input
                  label="New Password"
                  type="password"
                  placeholder="••••••••"
                  {...passwordForm.register('password')}
                  error={passwordForm.formState.errors.password?.message?.toString()}
                />
                <Input
                  label="Confirm Password"
                  type="password"
                  placeholder="••••••••"
                  {...passwordForm.register('confirmPassword')}
                  error={passwordForm.formState.errors.confirmPassword?.message?.toString()}
                />
                <Button type="submit" fullWidth size="lg" isLoading={isLoading} className="mt-2">
                  Reset Password
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
